import React, { useEffect, useMemo, useState } from 'react';
import { FiSearch, FiRefreshCw, FiRotateCcw, FiXCircle, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
  StatCard,
} from '../../components/UI';
import { usePagination } from '../../hooks';
import {
  listSubscriptions,
  refundSubscription,
  cancelSubscription,
  changeSubscriptionTier,
  getSubscriptionAnalytics,
  reconcileSubscriptions,
} from '../../services/adminService';
import styles from './Subscriptions.module.css';

const TIER_OPTIONS = [
  { value: 'paw_print', label: 'Paw Print (free)' },
  { value: 'fetch', label: 'Fetch' },
  { value: 'alpha', label: 'Alpha' },
  { value: 'business_starter', label: 'Business Starter' },
  { value: 'pack_leader', label: 'Pack Leader' },
  { value: 'vet_pro', label: 'Vet Pro' },
];

const BILLING_CYCLE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const RANGE_OPTIONS = [
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 365, label: '1y' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const formatMoney = (amount, currency = 'EUR') => {
  if (amount == null) return '-';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${amount} ${currency}`;
  }
};

const Subscriptions = () => {
  const [rangeDays, setRangeDays] = useState(90);
  const [data, setData] = useState({ transactions: [], stats: {}, series: [] });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [reconciling, setReconciling] = useState(false);
  const [reconcileMsg, setReconcileMsg] = useState('');

  // Refund flow
  const [refundTx, setRefundTx] = useState(null);
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [refundLoading, setRefundLoading] = useState(false);
  // Partial refund support — empty string means "full amount" (server uses
  // the original transaction amount). Numeric value (in major-unit currency,
  // e.g. 4.99 EUR) refunds that amount.
  const [refundAmount, setRefundAmount] = useState('');

  // Force-cancel (no refund) flow
  const [cancelTx, setCancelTx] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // Change tier flow
  const [tierTx, setTierTx] = useState(null);
  const [newTier, setNewTier] = useState('fetch');
  const [newCycle, setNewCycle] = useState('monthly');
  const [tierLoading, setTierLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const [list, ana] = await Promise.all([
        listSubscriptions({ sinceDays: rangeDays }),
        getSubscriptionAnalytics().catch((err) => {
          console.warn('Analytics load failed:', err.message);
          return null;
        }),
      ]);
      setData(list);
      if (ana) setAnalytics(ana);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDays]);

  // On-demand reconciliation. The nightly job does this automatically; this is
  // for when you want it now. Previews first, then asks before writing, because
  // the write downgrades real users to the free tier.
  const runReconcile = async () => {
    setReconciling(true);
    setError('');
    try {
      const preview = await reconcileSubscriptions({ dryRun: true });
      if (!preview.candidates) {
        setReconcileMsg('Nothing to reconcile — every subscription is within its period.');
        return;
      }
      const ok = window.confirm(
        `${preview.candidates} subscriptions are past their period end.\n\n` +
          `The store confirms ${preview.expired} as expired` +
          (preview.renewed ? `, and ${preview.renewed} actually renewed` : '') +
          (preview.unresolved ? `. ${preview.unresolved} could not be checked and will be left alone` : '') +
          `.\n\nApply this? Expired users are downgraded to the free tier.`
      );
      if (!ok) return;
      const res = await reconcileSubscriptions({ dryRun: false });
      setReconcileMsg(
        `Reconciled: ${res.expired} expired, ${res.renewed} renewed, ${res.unresolved} unresolved.`
      );
      await refresh();
    } catch (err) {
      setError(`Reconcile failed: ${err.message}`);
    } finally {
      setReconciling(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelTx?.userId) return;
    setCancelLoading(true);
    try {
      await cancelSubscription({
        userId: cancelTx.userId,
        reason: cancelReason || 'Admin force-cancel (no refund)',
      });
      setCancelTx(null);
      setCancelReason('');
      await refresh();
    } catch (err) {
      setError(`Cancel failed: ${err.message}`);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleChangeTier = async () => {
    if (!tierTx?.userId) return;
    setTierLoading(true);
    try {
      await changeSubscriptionTier({
        userId: tierTx.userId,
        tier: newTier,
        billingCycle: newCycle,
        reason: `Admin tier change → ${newTier} (${newCycle})`,
      });
      setTierTx(null);
      await refresh();
    } catch (err) {
      setError(`Tier change failed: ${err.message}`);
    } finally {
      setTierLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!refundTx) return;
    setRefundLoading(true);
    try {
      // Partial: parse the input as a number; CF expects amount in the same
      // currency unit Stripe was charged in. Empty input = omit and refund
      // the original full amount.
      const partial = refundAmount.trim();
      const amount = partial ? Number(partial) : undefined;
      if (partial && (isNaN(amount) || amount <= 0)) {
        throw new Error('Refund amount must be a positive number');
      }
      if (amount && refundTx.amount && amount > Number(refundTx.amount)) {
        throw new Error('Refund amount cannot exceed the original charge');
      }
      await refundSubscription({
        transactionId: refundTx.id,
        amount,
        reason: refundReason,
      });
      setRefundTx(null);
      setRefundReason('requested_by_customer');
      setRefundAmount('');
      refresh();
    } catch (err) {
      console.error(err);
      setError(`Refund failed: ${err.message}`);
    } finally {
      setRefundLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const list = data.transactions || [];
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (t) =>
        (t.id || '').toLowerCase().includes(q) ||
        (t.userId || '').toLowerCase().includes(q) ||
        (t.planId || t.plan || '').toLowerCase().includes(q) ||
        (t.status || '').toLowerCase().includes(q)
    );
  }, [data, searchQuery]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  const planCountsList = Object.entries(data.stats?.planCounts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  const csvColumns = [
    { key: 'id', label: 'ID' },
    { key: 'userId', label: 'User UID' },
    { key: 'planId', label: 'Plan', accessor: (t) => t.planId || t.plan || '' },
    { key: 'amount', label: 'Amount' },
    { key: 'currency', label: 'Currency' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Date', accessor: (t) => t.createdAt },
  ];

  const statusBadge = (status) => {
    const variants = {
      completed: 'success',
      succeeded: 'success',
      pending: 'warning',
      failed: 'danger',
      refunded: 'inactive',
      cancelled: 'inactive',
    };
    return <Badge variant={variants[status] || 'inactive'}>{status || 'unknown'}</Badge>;
  };

  const columns = [
    {
      key: 'id',
      title: 'Tx ID',
      render: (id) => <code className={styles.code}>{id?.slice(0, 10) || '-'}</code>,
    },
    { key: 'userId', title: 'User', render: (v) => v?.slice(0, 12) + (v?.length > 12 ? '…' : '') || '-' },
    {
      key: 'planId',
      title: 'Plan',
      render: (_, row) => <Badge variant="secondary">{row.planId || row.plan || 'unknown'}</Badge>,
    },
    {
      key: 'amount',
      title: 'Amount',
      render: (v, row) => formatMoney(v, row.currency),
    },
    { key: 'status', title: 'Status', render: (v) => statusBadge(v) },
    { key: 'createdAt', title: 'Date', render: (v) => formatDate(v) },
    {
      key: 'actions',
      title: '',
      width: '160px',
      render: (_, row) => {
        const refundable = ['completed', 'succeeded', 'paid'].includes(row.status);
        const cancellable = !!row.userId && row.status !== 'cancelled' && row.status !== 'refunded';
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            {refundable && (
              <Button
                variant="ghost"
                size="sm"
                icon={<FiRotateCcw />}
                onClick={() => setRefundTx(row)}
                title="Refund via Stripe"
              />
            )}
            {cancellable && (
              <Button
                variant="ghost"
                size="sm"
                icon={<FiXCircle />}
                onClick={() => setCancelTx(row)}
                title="Force-cancel (no refund)"
              />
            )}
            {row.userId && (
              <Button
                variant="ghost"
                size="sm"
                icon={<FiArrowRight />}
                onClick={() => {
                  setNewTier(row.tier || row.planId || 'fetch');
                  setNewCycle(row.billingCycle || 'monthly');
                  setTierTx(row);
                }}
                title="Change tier"
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Subscriptions & Revenue</h1>
        </div>
        <p className={styles.subtitle}>Subscription transactions and revenue analytics</p>
      </div>

      {error && (
        <Card>
          <div style={{ color: '#f87171' }}>Error: {error}</div>
        </Card>
      )}

      {reconcileMsg && (
        <Card>
          <div style={{ color: '#34d399', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>{reconcileMsg}</span>
            <button
              type="button"
              onClick={() => setReconcileMsg('')}
              style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
        </Card>
      )}

      <div className={styles.statsGrid}>
        <StatCard
          title="Total Revenue (all-time)"
          value={formatMoney(data.stats?.totalRevenueAll || 0, 'EUR')}
          color="primary"
        />
        <StatCard
          title={`Net Revenue (${rangeDays}d)`}
          value={formatMoney(data.stats?.netRevenueWindow || 0, 'EUR')}
          color="secondary"
        />
        <StatCard
          title="MRR (last 30d)"
          value={formatMoney(data.stats?.mrr || 0, 'EUR')}
          color="tertiary"
        />
        <StatCard
          title="ARPU"
          value={formatMoney(data.stats?.arpu || 0, 'EUR')}
          color="warning"
        />
        <StatCard
          title="Paying Users"
          value={data.stats?.payingUsers ?? 0}
          color="primary"
        />
        <StatCard
          title="Successful Tx"
          value={data.stats?.completedCount ?? 0}
          color="secondary"
        />
        <StatCard
          title="Refunds"
          value={`${formatMoney(data.stats?.refundsWindow || 0, 'EUR')}`}
          color="warning"
        />
        <StatCard
          title="Refund Rate"
          value={`${((data.stats?.refundRate || 0) * 100).toFixed(1)}%`}
          color="tertiary"
        />
      </div>

      <Card
        title="Revenue Trend"
        subtitle={`Daily completed revenue · last ${rangeDays} days`}
        action={
          <div className={styles.rangePicker}>
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={rangeDays === opt.value ? styles.active : ''}
                onClick={() => setRangeDays(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        }
      >
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data.series || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="date"
              stroke="#666"
              fontSize={12}
              tickFormatter={(v) => (rangeDays > 90 ? v.slice(0, 7) : v.slice(5))}
              minTickGap={32}
              interval="preserveStartEnd"
            />
            <YAxis stroke="#666" fontSize={12} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1a1a1a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
              }}
              labelStyle={{ color: '#f5f5f5' }}
              formatter={(value) => formatMoney(value, 'EUR')}
            />
            <Line
              type="monotone"
              dataKey="amount"
              name="Revenue"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: '#10b981', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, fill: '#34d399' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className={styles.twoCol}>
        {planCountsList.length > 0 && (
          <Card title="Top Plans by Transactions" subtitle="Most subscribed-to plans">
            <div className={styles.plans}>
              {planCountsList.map(([plan, count]) => (
                <div key={plan} className={styles.planRow}>
                  <span className={styles.planName}>{plan}</span>
                  <span className={styles.planCount}>{count}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {(data.planRevenue || []).length > 0 && (
          <Card title="Top Plans by Revenue" subtitle="Highest-grossing plans">
            <div className={styles.plans}>
              {data.planRevenue.map((row) => (
                <div key={row.plan} className={styles.planRow}>
                  <span className={styles.planName}>{row.plan}</span>
                  <span className={styles.planRevenueAmt}>{formatMoney(row.total, 'EUR')}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {(data.topSubscribers || []).length > 0 && (
        <Card title={`Top Subscribers (${rangeDays}d)`} subtitle="By total successful spend in the selected window">
          <div className={styles.plans}>
            {data.topSubscribers.map((row, i) => (
              <div key={row.userId} className={styles.planRow}>
                <span className={styles.planName}>
                  <span style={{ color: '#666', marginRight: 8 }}>#{i + 1}</span>
                  {row.email || row.userId}
                </span>
                <span className={styles.planRevenueAmt}>{formatMoney(row.total, 'EUR')}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {analytics && (
        <div className={styles.statsGrid}>
          <StatCard
            title="Churn Rate (30d)"
            value={`${((analytics.churn?.churnRate || 0) * 100).toFixed(1)}%`}
            color="warning"
          />
          <StatCard
            title="Lost Payers (30d)"
            value={analytics.churn?.lostPayers ?? 0}
            color="warning"
          />
          <StatCard
            title="Retained Payers (30d)"
            value={analytics.churn?.retainedPayers ?? 0}
            color="secondary"
          />
          <StatCard
            title="New Payers (30d)"
            value={analytics.churn?.newPayers ?? 0}
            color="primary"
          />
        </div>
      )}

      {analytics?.funnel && (
        <Card title="Conversion Funnel" subtitle="Sign-up → first payment">
          <div className={styles.funnel}>
            <div className={styles.funnelStep}>
              <div className={styles.funnelLabel}>Total Users</div>
              <div className={styles.funnelValue}>{analytics.funnel.totalUsers.toLocaleString()}</div>
              <div
                className={styles.funnelBar}
                style={{ width: '100%', background: '#0ea5e9' }}
              />
            </div>
            <div className={styles.funnelStep}>
              <div className={styles.funnelLabel}>Paying Users</div>
              <div className={styles.funnelValue}>{analytics.funnel.paidUsers.toLocaleString()}</div>
              <div
                className={styles.funnelBar}
                style={{
                  width: `${Math.max(2, (analytics.funnel.conversionRate || 0) * 100)}%`,
                  background: '#10b981',
                }}
              />
            </div>
            <div className={styles.funnelSummary}>
              Conversion rate:{' '}
              <strong>{((analytics.funnel.conversionRate || 0) * 100).toFixed(2)}%</strong>
            </div>
          </div>
        </Card>
      )}

      {analytics?.cohorts && analytics.cohorts.some((c) => c.size > 0) && (
        <Card
          title="Cohort Retention"
          subtitle="% of users who paid in subsequent months · last 6 months"
        >
          <div className={styles.cohortTableWrap}>
            <table className={styles.cohortTable}>
              <thead>
                <tr>
                  <th>Cohort</th>
                  <th>Size</th>
                  {analytics.cohorts[0].retention.map((_, i) => (
                    <th key={i}>M{i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analytics.cohorts.map((c) => (
                  <tr key={c.month}>
                    <td className={styles.cohortMonth}>{c.month}</td>
                    <td>{c.size}</td>
                    {c.retention.map((pct, i) => {
                      const v = pct * 100;
                      const bg = `rgba(14, 165, 233, ${Math.min(0.9, 0.1 + pct * 0.8)})`;
                      return (
                        <td key={i} className={styles.cohortCell} style={{ background: bg }}>
                          {v > 0 ? `${v.toFixed(0)}%` : '·'}
                        </td>
                      );
                    })}
                    {Array.from({ length: 6 - c.retention.length }, (_, i) => (
                      <td key={`empty-${i}`} className={styles.cohortEmpty}>
                        —
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={styles.cohortNote}>
            Each row = a cohort of users whose <em>first</em> successful payment was that month.
            <br />
            M0 = first payment month; M1, M2 … = % of that cohort who also paid in subsequent months.
          </p>
        </Card>
      )}

      <Card
        title="Transactions"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="sm" icon={<FiRefreshCw />} onClick={refresh} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<FiCheckCircle />}
              onClick={runReconcile}
              disabled={reconciling}
              title="Re-check subscriptions past their period end against the App Store / Play / Stripe"
            >
              {reconciling ? 'Reconciling…' : 'Reconcile'}
            </Button>
            <CsvExportButton rows={filtered} columns={csvColumns} filename={`dierend-subscriptions-${rangeDays}d`} />
          </div>
        }
      >
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search by id, user, plan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
        </div>

        <Table columns={columns} data={pageData} loading={loading} emptyMessage="No transactions" />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={!!refundTx}
        onClose={() => setRefundTx(null)}
        title="Refund transaction"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRefundTx(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              icon={<FiRotateCcw />}
              loading={refundLoading}
              onClick={handleRefund}
            >
              Refund via Stripe
            </Button>
          </>
        }
      >
        {refundTx && (
          <div className={styles.refundBody}>
            <p>
              You are about to refund up to <strong>{formatMoney(refundTx.amount, refundTx.currency)}</strong>{' '}
              to user <code>{refundTx.userId}</code>.
            </p>
            <p style={{ color: '#888', fontSize: 13 }}>
              This calls Stripe immediately. The transaction status will move to <code>refunded</code>{' '}
              and the action is logged in the audit log.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label className={styles.refundLabel}>
                Amount ({refundTx.currency || 'EUR'}) — leave empty for full refund
              </label>
              <input
                type="text"
                inputMode="decimal"
                className={styles.refundSelect}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={`${refundTx.amount ?? ''}`}
              />
            </div>
            <div>
              <label className={styles.refundLabel}>Reason</label>
              <select
                className={styles.refundSelect}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              >
                <option value="requested_by_customer">Requested by customer</option>
                <option value="duplicate">Duplicate charge</option>
                <option value="fraudulent">Fraudulent</option>
              </select>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!cancelTx}
        onClose={() => setCancelTx(null)}
        title="Force-cancel subscription (no refund)"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelTx(null)}>
              Back
            </Button>
            <Button
              variant="danger"
              icon={<FiXCircle />}
              loading={cancelLoading}
              onClick={handleCancel}
            >
              Cancel subscription
            </Button>
          </>
        }
      >
        {cancelTx && (
          <div className={styles.refundBody}>
            <p>
              Cancel subscription for user <code>{cancelTx.userId}</code> without issuing a
              refund. The subscription <code>status</code> moves to <code>cancelled</code> and the
              action is logged in the audit log.
            </p>
            <p style={{ color: '#888', fontSize: 13 }}>
              Use Refund instead if the user is owed money back. This is the right action for a
              support escalation where the customer is moving off the plan but the past charge
              stands.
            </p>
            <div>
              <label className={styles.refundLabel}>Reason (optional)</label>
              <input
                type="text"
                className={styles.refundSelect}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. customer-requested cancellation, fraud follow-up"
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!tierTx}
        onClose={() => setTierTx(null)}
        title="Change subscription tier"
        footer={
          <>
            <Button variant="ghost" onClick={() => setTierTx(null)}>
              Back
            </Button>
            <Button
              variant="primary"
              icon={<FiArrowRight />}
              loading={tierLoading}
              onClick={handleChangeTier}
            >
              Apply change
            </Button>
          </>
        }
      >
        {tierTx && (
          <div className={styles.refundBody}>
            <p>
              Override the tier for user <code>{tierTx.userId}</code>. This is an admin grant —
              the user does not pay anything; their subscription record is updated and audit-logged.
            </p>
            <p style={{ color: '#888', fontSize: 13 }}>
              Use this for comping partners, fixing miscategorizations, or downgrading after a
              refund. For paid upgrades the user should use the in-app subscription flow.
            </p>
            <div>
              <label className={styles.refundLabel}>Tier</label>
              <select
                className={styles.refundSelect}
                value={newTier}
                onChange={(e) => setNewTier(e.target.value)}
              >
                {TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 12 }}>
              <label className={styles.refundLabel}>Billing cycle</label>
              <select
                className={styles.refundSelect}
                value={newCycle}
                onChange={(e) => setNewCycle(e.target.value)}
              >
                {BILLING_CYCLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Subscriptions;
